import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type { SubRoute } from '@ipen-portal/plugin-common-react';
import type { FC } from 'react';

import { LoadingWrapper } from '@ipen-portal/plugin-common-react';
import { List } from '@mui/material';
import { useMemo } from 'react';
import { tss } from 'tss-react/mui';

import { NavMenuItem } from '../NavMenuItem';
import { BackNavItem } from './components/BackNavItem';
import { CollapsableNavGroup } from './components/CollapsableNavGroup';
import { processGroupedTabs } from './utils/processGroupedTabs';

const useStyles = tss
  .withParams<Props>()
  .create(({ maxHeightOffset, minHeightOffset, theme }) => ({
    list: {
      padding: 0,
    },
    root: {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0px 3px 6px #00000059',
      flexShrink: 0,
      height: '100%',
      maxHeight: `calc(100vh - ${maxHeightOffset}px)`,
      minHeight: `calc(100vh - ${minHeightOffset}px)`,
      overflowY: 'auto',
      position: 'sticky',
      top: 0,
      width: 256,
      zIndex: 0,
    },
  }));

type Props = {
  maxHeightOffset: number;
  minHeightOffset: number;
};

export const SubRouteNavigation: FC<
  Props & {
    groupDefinitions?: EntityContentGroupDefinitions;
    loading?: boolean;
    selectedSubPath?: string;
    selectedSubRoute?: SubRoute;
    subRoutes: readonly SubRoute[];
    titleOrder: Record<string, number>;
  }
> = ({
  groupDefinitions,
  loading,
  maxHeightOffset,
  minHeightOffset,
  selectedSubPath,
  selectedSubRoute,
  subRoutes,
  titleOrder,
}) => {
  const { classes } = useStyles({ maxHeightOffset, minHeightOffset });

  const groupedTabs = useMemo(
    () =>
      processGroupedTabs(
        subRoutes,
        selectedSubRoute,
        selectedSubPath,
        groupDefinitions,
        titleOrder,
      ),
    [
      selectedSubPath,
      selectedSubRoute,
      subRoutes,
      groupDefinitions,
      titleOrder,
    ],
  );

  if (!subRoutes.length) {
    return null;
  }

  return (
    <nav aria-label="Sub Navigation" className={classes.root}>
      <List className={classes.list}>
        <BackNavItem />
        <LoadingWrapper loading={loading}>
          {groupedTabs.map(item => {
            if ('tabs' in item) {
              if (item.tabs.length > 1) {
                return (
                  <CollapsableNavGroup
                    key={item.title}
                    {...item}
                    titleOrder={titleOrder}
                  />
                );
              }
              const navItem = item.tabs[0];
              if (!navItem) {
                return null;
              }
              return (
                <NavMenuItem
                  active={navItem.active}
                  isSubmenu
                  item={navItem}
                  key={navItem.target}
                  removeLeadingSlash
                />
              );
            }
            return (
              <NavMenuItem
                active={item.active}
                isSubmenu
                item={item}
                key={item.target}
                removeLeadingSlash
              />
            );
          })}
        </LoadingWrapper>
      </List>
    </nav>
  );
};
